import React, { FC, useContext, useMemo, useState } from "react";
import { Font,  Page, Text, View, Document, StyleSheet, Image, pdf } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import zioncom from 'images/system/zioncom.png'
import { App, Button, Dropdown, Tooltip } from "antd";
import { ChangeEnum, OperateEnum } from "@/types/inventory";
import { numFormmater } from "@/pages/Requirement/util";
import JsBarcode from "jsbarcode";
import convertTo2DArray from "@/utils/convert2double";
import { getFileUrl } from "@/apis/file";
import { DictContext } from "@/layout/context/DictContext";
import { OrderItemV2 } from "@/types/inventoryV2";
import { PurchaseOrder } from "@/types/purchase";
import { changeTypes, zhVnChangeTypes } from "@/pages/Warehouse/utils";
import truncateString from "@/utils/stringOver";
import { SourceTypeEnum } from "@/types/common";
import { getSourceNumber } from "@/utils/getInventorySourceNo";
import { dateFormat } from "@/utils/format";
import { RootState } from "@/store";
import { useSelector } from "react-redux";
import { upload } from "@/utils/file/upload";
import { docAdd } from "@/apis/doc";
import { DocTypeEnum } from "@/types/doc";
import { orderPrint, orderQcPrint } from "@/apis/inventoryv2";
import { computedPrint, initPrintSetting } from "@/layout/HomeLayout/utils";
import fontSrc from '@/assets/fonts/msyh.ttf';
import vnSrc from '@/assets/fonts/NotoSans-Regular.ttf';
import krSrc from '@/assets/fonts/NotoSansKR-Regular.otf';

// 注册支持中文的字体（重复注册幂等；若启动预热失败，首次渲染时在此兜底加载）
Font.register({
  family: "NotoSansSC",
  src: fontSrc
});

Font.register({
  family: "VnFontSrc",
  src: vnSrc
});

Font.register({
  family: "KoFontSrc",
  src: krSrc
});

// 定义 PDF 文档样式
const styles = StyleSheet.create({
  // 页面样式
  page: {
    position: "relative",
    fontFamily: ["VnFontSrc", "KoFontSrc", "NotoSansSC"],
  },

  // 表格样式
  table: {
    display: "table" as any,
    width: "100%",
    borderStyle: "solid",
    borderLeftWidth: 1,
    borderTopWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderColor: "black"
  },
  tableRow: {
    flexDirection: "row"
  },

  // 单元格基础样式
  tableCol: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderStyle: "solid",
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: "black",
    padding: 2,
    fontSize: 6,
    letterSpacing: -1,
    color: 'black'
  },

  // 表头单元格
  tableTopTitle: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderStyle: "solid",
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: "black",
    padding: 2,
    fontSize: 6,
    letterSpacing: -1,
    color: 'black'
  },

  // 表头文本
  tableHeaderCell: {
    fontFamily: ["VnFontSrc", "KoFontSrc", "NotoSansSC"],
    fontSize: 6
  },

  // 合并单元格
  mergeCell: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    padding: 0
  },

  // 文本样式
  fontStyle: {
    textAlign: 'center'
  },

  // 页面标题
  pageTitle: {
    fontSize: 14,
    textAlign: 'center',
    letterSpacing: 0,
    paddingTop: 8,
  },

  // 标题区域容器
  titleSection: {
    position: 'relative' as any,
    marginBottom: 8
  },

  // 右上角条码样式
  titleBarcode: {
    position: 'absolute' as any,
    right: 0,
    top: 0,
    flexDirection: 'column',
    alignItems: 'center'
  },

  // 条码文字
  barcodeText: {
    fontSize: 5,
    marginTop: -3
  },

  // 底部信息样式
  bottomInfo: {
    width: "100%",
    flexDirection: "row",
    marginTop: 12,
    paddingHorizontal: 24
  },
  bottomItem: {
    flex: 1,
    textAlign: "left"
  },
  bottomText: {
    fontSize: 8
  },

  // 页脚
  footer: {
    position: "absolute",
    bottom: 24,
    left: 0,
    right: 0,
    fontSize: 6,
  },
  printInfo: {
    position: "absolute",
    bottom: 24,
    right: 30,
    fontSize: 6,
    color: "#000",
    textAlign: "right",
  },
  footerItem: { width: '33.3%', fontSize: 6 },
  headerItem: { marginRight: 30, fontSize: 6, textAlign: 'left' }
});

// 填充空格
function getBlankWord(word: string, type = false) {
  if (type) return String(word).replace(/./g, (word) => word + ' ');
  else return String(word).replace(/./g, (word) => word + '');
}

// 打印类型枚举
export enum PrintType {
  PURCHASE = "PURCHASE",
  NORMAL = "NORMAL",
  OUT = "OUT",
  SUP = "SUP"
}

interface PrintQCProps {
  selectedRows: Array<OrderItemV2>;
  disabled: boolean;
  onStart: () => void;
  onEnd: () => void;
  dictAddress?: string;
  orderMark?: string;
  itemMark?: string;
  purOrder?: PurchaseOrder;
  showHeader?: boolean;
  printType: PrintType;
  purOrderNo?: string;
  showBottom?: boolean;
}

type CodeUrlOrder = OrderItemV2 & {
  codeStr: string;
  codeUrl: string;
}

// 生成 PDF 下载链接
const PrintQC: FC<PrintQCProps> = ({
  selectedRows, disabled, onStart, onEnd, dictAddress, orderMark = '', itemMark = '', purOrder, showHeader = false, printType, purOrderNo, showBottom = false
}) => {
  const { printSetting } = useContext(DictContext);

  const { t } = useTranslation();
  const curUser = useSelector((state: RootState) => state.userReducer.curUser)

  const { width: printWitdh, height: printHeight } = computedPrint();
  const { width: settingWitdh, height: settingHeight } = initPrintSetting();

  const printerStr = useMemo(() => {
    let time = dateFormat(dayjs(), true);
    if (!!curUser.name1 && !!curUser.name2) return `${curUser.name1}(${curUser.name2}) ${time}`
    else return `${curUser.name1 || curUser.name2 || curUser.mobile} ${time}`
  }, [curUser]);

  const { orderLabel } = useMemo(() => {
    let order = selectedRows[0]?.order;
    let orderLabel = ''
    if (order) {
      let info = zhVnChangeTypes.find((item) => item.value === order.type);
      if (order.opType === OperateEnum.TEMPORARY) {
        orderLabel = [ChangeEnum.IN, ChangeEnum.PRE_IN].includes(order.type)
          ? `${t('warehouse.tempIn', { lng: "zh" })}/${t('warehouse.tempIn', { lng: "vn" })}`
          : `${t('warehouse.otherOut', { lng: "zh" })}/${t('warehouse.otherOut', { lng: "vn" })}`
      } else orderLabel = info?.label ?? ''
    }
    return {
      orderLabel
    }
  }, [selectedRows, t, changeTypes])
  const { notification } = App.useApp();
  const [loading, setLoading] = useState<boolean>(false)

  const handleCode: (orderNo: string, lot: string) => Promise<{ codeUrl: string, codeStr: string }> = (orderNo: string, lot) => {
    return new Promise((resolve, reject) => {
      const canvasDom = document.createElement("canvas")
      JsBarcode(canvasDom, orderNo, {
        format: "CODE128",
        width: 10,
        height: 28,
        displayValue: false,
        margin: 0,
        marginBottom: 8
      })
      canvasDom.toBlob((blob) => {
        canvasDom.remove()
        if (blob) resolve({ codeUrl: URL.createObjectURL(blob), codeStr: lot })
        else reject("条码生成失败")
      }, 'image/png')

    })
  }

  const { picUrl, name } = useMemo(() => {
    let [picUrl, name] = ['', ''];
    if (printSetting?.length > 0) {
      printSetting.forEach(item => {
        if (item.key === "companyName" && item.value) name = item.value
        if (item.key === "companyLogo" && item.value) picUrl = getFileUrl(item.value) || ''
      })
    }

    return { picUrl, name };

  }, [printSetting])

  const getReceiverInfo = (order: PurchaseOrder | null) => {
    let supplierPrint = ""
    let mobilePrint = ""
    let recver = ""
    supplierPrint = order?.receiver || purOrder?.receiver || ''
    mobilePrint = order?.receiverMobile || purOrder?.receiverMobile || ''
    recver = order?.receiverContact || purOrder?.receiverContact || ''
    return {
      supplierPrint,
      mobilePrint,
      recver
    }
  }

  const MyDocument = (selRows: CodeUrlOrder[], purOrderBarcode?: string) => {
    if (selRows && selRows.length > 0) {
      let seq = 0;
      return <Document>
        <Page size="A4" orientation="landscape" style={[styles.page, { padding: 28 }]} wrap>
          <View>
            {/* 标题部分 */}
            {
              (
                (printType === PrintType.PURCHASE && purOrderBarcode)
                || (printType === PrintType.OUT && showBottom && purOrderBarcode)
              ) ? (
                <View style={styles.titleSection}>
                  <Text style={{ ...styles.fontStyle, ...styles.pageTitle }}>
                    {name ?? '吉翁科技（深圳）有限公司'}
                  </Text>
                  <Text style={{ fontSize: 10, textAlign: "center" }}>
                    {orderLabel}
                  </Text>
                  <View style={styles.titleBarcode}>
                    <Image src={purOrderBarcode} style={{ width: 100, height: 28 }} />
                    <Text style={styles.barcodeText}>{
                      printType === PrintType.PURCHASE ? purOrderNo :
                        printType === PrintType.OUT ? selectedRows[0]?.order?.number : ''
                    }</Text>
                  </View>
                </View>
              ) : (
                <View style={styles.titleSection}>
                  <Text style={{ ...styles.fontStyle, ...styles.pageTitle }}>{name ?? '吉翁科技（深圳）有限公司'}</Text>
                  <Text style={{ ...styles.fontStyle, ...styles.pageTitle }}>{printType === PrintType.SUP ? `${t('purchase.checkoutOrder', { lng: "zh" })}/${t('purchase.checkoutOrder', { lng: "vn" })}` : orderLabel}</Text>
                  <View style={styles.titleBarcode}>
                    <Image src={purOrderBarcode} style={{ width: 100, height: 28 }} />
                    <Text style={styles.barcodeText}>{
                      selectedRows[0]?.order?.number ?? ''
                    }</Text>
                  </View>
                </View>
              )
            }
          </View>
          <View style={{ ...styles.table, borderLeftWidth: 0, flexShrink: 0 }}>
            {/* 表头 */}
            <View style={{ flexDirection: 'row' }} fixed render={({ pageNumber }) => {
              return pageNumber === 1 ? null : <View style={{ height: 1, backgroundColor: "#000", width: "100%" }} />
            }}></View>
            <View style={{ flexDirection: 'row', borderLeftWidth: 1 }} fixed>
              <View style={{ ...styles.tableTopTitle, width: '28%', textAlign: 'left' }}><Text style={{ width: "100%" }}>{getBlankWord(`${t('purchase.sendTimePrint', { lng: "zh" })}(${t('purchase.sendTimePrint', { lng: "vn" })}):`, true)}{getBlankWord(selRows[0]?.order.preTime ? dayjs(selRows[0]?.order.preTime).format('YYYY-MM-DD') : '', true)}</Text></View>
              <View style={{ ...styles.tableTopTitle, width: '28%', textAlign: 'left' }}><Text style={{ width: "100%" }}>{getBlankWord(`${t('purchase.supplierPrint', { lng: "zh" })}(${t('purchase.supplierPrint', { lng: "vn" })}):` + getReceiverInfo(selRows[0].sourceType === SourceTypeEnum.PURCHASE_ITEM ? selRows[0]?.source?.order || null : null).supplierPrint, true)}</Text></View>
              <View style={{ ...styles.tableTopTitle, width: '30%', textAlign: 'left' }}><Text style={{ width: "100%" }}>{getBlankWord(`${t('purchase.mobilePrint', { lng: "zh" })}(${t('purchase.mobilePrint', { lng: "vn" })}):` + getReceiverInfo(selRows[0].sourceType === SourceTypeEnum.PURCHASE_ITEM ? selRows[0]?.source?.order || null : null).mobilePrint, true)}</Text></View>
              <View style={{ ...styles.tableTopTitle, width: '14%', textAlign: 'left' }}><Text style={{ width: "100%" }}>{getBlankWord(`${t('purchase.recver', { lng: "zh" })}(${t('purchase.recver', { lng: "vn" })}):` + getReceiverInfo(selRows[0].sourceType === SourceTypeEnum.PURCHASE_ITEM ? selRows[0]?.source?.order || null : null).recver, true)}</Text></View>
            </View>
            <View style={{ flexDirection: 'row', borderLeftWidth: 1 }} fixed>
              <View style={{ ...styles.tableCol, width: '6%', borderTopWidth: 0 }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell }}>{getBlankWord(t('purchase.seq', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell }}>{getBlankWord(t('purchase.seq', { lng: "vn" }))}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '22%' }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell }}>{
                  printType !== PrintType.OUT ? t('purchase.sendNum', { lng: "zh" })
                    : t('purchase.outLot', { lng: "zh" })
                }</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell }}>{
                  printType !== PrintType.OUT ? t('purchase.sendNum', { lng: "vn" })
                    : t('purchase.outLot', { lng: "vn" })
                }</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '12%' }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printCode', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printCode', { lng: "vn" }))}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '8%' }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printName', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printName', { lng: "vn" }))}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '20%' }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printModel', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printModel', { lng: "vn" }))}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '4%' }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printUnit', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printUnit', { lng: "vn" }))}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '7%' }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printNum', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printNum', { lng: "vn" }))}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '7%' }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.qcNum', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.qcNum', { lng: "vn" }))}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '7%' }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.qcNo', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.qcNo', { lng: "vn" }))}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '7%' }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.qcOk', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.qcOk', { lng: "vn" }))}</Text>
              </View>
            </View>
            {/* 表身 */}
            {selRows.map((item, rowIndex) => (
              <View style={[styles.tableRow, { borderLeftWidth: 1 }]} key={rowIndex} wrap={false}>
                <View style={{ ...styles.tableCol, width: '6%' }}><Text style={[styles.fontStyle, { letterSpacing: 0 }]}>{++seq}</Text></View>
                <View style={{ ...styles.tableCol, width: '22%', flexDirection: "column" }}>
                  {item.codeUrl && <Image src={item.codeUrl} style={{ width: "90%", height: 28 }} />}
                  <Text style={{ ...styles.fontStyle, letterSpacing: -1, fontSize: !!item.codeUrl ? 5 : 6, marginTop: !!item.codeUrl ? -6 : 0 }}>{getBlankWord(item.codeStr, true)}</Text>
                  <Text style={{ ...styles.fontStyle, letterSpacing: 0, fontSize: !!item.codeUrl ? 5 : 6, marginTop: -1 }}>{getSourceNumber(item)}</Text>
                </View>
                <View style={{ ...styles.tableCol, width: '12%' }}><Text style={{ ...styles.fontStyle, letterSpacing: -1 }}>{getBlankWord(item?.product?.code || purOrder?.product?.code || '', true)}</Text></View>
                <View style={{ ...styles.tableCol, width: '8%' }}><Text style={{ ...styles.fontStyle, textAlign: 'left', letterSpacing: -1 }}>{getBlankWord(item?.product?.name || purOrder?.product?.name || '', true)}</Text></View>
                <View style={{ ...styles.tableCol, width: '20%' }}><Text style={{ ...styles.fontStyle, textAlign: 'left', width: "100%", letterSpacing: -1 }}>{getBlankWord(truncateString(item?.product?.model || purOrder?.product?.model || '', 38), true)}</Text></View>
                <View style={{ ...styles.tableCol, width: '4%' }}><Text style={{ ...styles.fontStyle, textAlign: 'left', width: "100%", letterSpacing: -1 }}>{getBlankWord(item?.product?.uom || purOrder?.product?.uom || '')}</Text></View>
                <View style={{ ...styles.tableCol, width: '7%' }}><Text style={{ ...styles.fontStyle, textAlign: 'left', width: "100%", letterSpacing: -1 }}>{getBlankWord(numFormmater(Number(item.count)), true)}</Text></View>
                <View style={{ ...styles.tableCol, width: '7%' }}></View>
                <View style={{ ...styles.tableCol, width: '7%' }}></View>
                <View style={{ ...styles.tableCol, width: '7%' }}></View>

                {/* <View style={{ ...styles.tableCol, ...styles.mergeCell, width: '15%', borderRightWidth: 1 }}>
                        <View style={{ flexDirection: 'row', justifyContent: 'flex-start', width: '100%' }}>
                          <View style={{ ...styles.tableCol, width: '40.3%', borderTopWidth: 0, borderLeftWidth: 0, borderBottomWidth: 0 }}><Text style={{ ...styles.fontStyle, letterSpacing: -1 }}>{getBlankWord(item?.product?.uom || purOrder?.product?.uom || '')}</Text></View>
                          <View style={{ ...styles.tableCol, width: '59.7%', borderTopWidth: 0, borderRightWidth: 0, borderBottomWidth: 0 }}><Text style={{ ...styles.fontStyle, letterSpacing: 0 }}>{numFormmater(Number(item.count))}</Text></View>
                        </View>
                        <View style={{ borderStyle: "solid", borderTopWidth: 1, borderBottomWidth: 0, borderLeftWidth: 0, borderRightWidth: 0, borderColor: "black", width: "100%", color: "black", fontSize: 6, letterSpacing: -1, textAlign: 'left', padding: 2 }}>
                          <Text>{getBlankWord(t('purchase.remark') ?? '', true) + "： "}{getBlankWord(orderMark && itemMark ? orderMark + '+' + itemMark : orderMark + itemMark, true)}</Text>
                        </View>
                      </View> */}
              </View>
            ))}
          </View>

          <View style={styles.bottomInfo}>
            <View style={styles.bottomItem}>
              <Text style={styles.bottomText}>{`${t('purchase.qc', { lng: "zh" })}(${t('purchase.qc', { lng: "vn" })})`}</Text>
            </View>
            <View style={styles.bottomItem}>
              <Text style={styles.bottomText}>{`${t('purchase.pg', { lng: "zh" })}(${t('purchase.pg', { lng: "vn" })})`}</Text>
            </View>
            <View style={styles.bottomItem}>
              <Text style={styles.bottomText}>{`${t('purchase.cg', { lng: "zh" })}(${t('purchase.cg', { lng: "vn" })})`}</Text>
            </View>
          </View>

          <Image src={picUrl || zioncom} style={{
            position: "absolute",
            top: 24,
            left: 30,
            flexGrow: 1,  // 让图片填充剩余空间
            height: 30,
            objectFit: "scale-down" // 让图片保持比例填充
          }} />
          {/* 添加分页 */}
          <Text fixed style={{ ...styles.fontStyle, ...styles.footer, zIndex: 99 }} render={({ pageNumber, totalPages }) => {
            return `P ${pageNumber} / ${totalPages}`
          }}>
          </Text>
          <Text fixed style={styles.printInfo} >{printerStr}</Text>
        </Page>
      </Document>
    }
    else return <Document />
  }
  const MyVerticalDocument = (selRows: CodeUrlOrder[], purOrderBarcode?: string) => {
    return <Document>
      <Page size={{ width: printWitdh, height: printHeight }} orientation="portrait" style={[styles.page, { paddingHorizontal: 4 }]} wrap >
        <View fixed render={({ pageNumber }) => {
          return pageNumber === 1 ? null : <View style={{ height: 1, backgroundColor: "#000" }} />
        }}>
        </View>
        <View>
          {/* 标题部分 */}
          {
            (printType === PrintType.PURCHASE && purOrderBarcode)
              || (printType === PrintType.OUT && showBottom && purOrderBarcode)
              ? (
                <View style={styles.titleSection}>
                  <Text style={{ ...styles.fontStyle, ...styles.pageTitle }}>
                    {name ?? '吉翁科技（深圳）有限公司'}
                  </Text>
                  <Text style={{ fontSize: 10, textAlign: "center" }}>
                    {orderLabel}
                    {'    '}
                    {
                      printType === PrintType.PURCHASE ? purOrderNo :
                        printType === PrintType.OUT ? selectedRows[0]?.order?.number : ''
                    }
                  </Text>
                </View>
              ) : (
                <View style={styles.titleSection}>
                  <Text style={{ ...styles.fontStyle, ...styles.pageTitle }}>{name ?? '吉翁科技（深圳）有限公司'}</Text>
                  <Text style={{ ...styles.fontStyle, fontSize: 10 }}>
                    {printType === PrintType.SUP ? `${t('purchase.checkoutOrder', { lng: "zh" })}/${t('purchase.checkoutOrder', { lng: "vn" })}` : orderLabel}
                    {'    '}
                    {selectedRows[0]?.order?.number ?? ''}
                  </Text>
                </View>
              )
          }
        </View>
        <View style={{ ...styles.table, flexShrink: 0, borderLeftWidth: 0 }}>
          {/* 表头 */}
          <View style={{ flexDirection: 'row', borderLeftWidth: 1 }}>
            <View style={{ ...styles.tableTopTitle, width: '28%', textAlign: 'left' }}><Text style={{ width: "100%" }}>{getBlankWord(`${t('purchase.sendTimePrint', { lng: "zh" })}(${t('purchase.sendTimePrint', { lng: "vn" })}):`, true)}{getBlankWord(selRows[0]?.order.preTime ? dayjs(selRows[0]?.order.preTime).format('YYYY-MM-DD') : '', true)}</Text></View>
            <View style={{ ...styles.tableTopTitle, width: '28%', textAlign: 'left' }}><Text style={{ width: "100%" }}>{getBlankWord(`${t('purchase.supplierPrint', { lng: "zh" })}(${t('purchase.supplierPrint', { lng: "vn" })}):` + getReceiverInfo(selRows[0].sourceType === SourceTypeEnum.PURCHASE_ITEM ? selRows[0]?.source?.order || null : null).supplierPrint, true)}</Text></View>
            <View style={{ ...styles.tableTopTitle, width: '30%', textAlign: 'left' }}><Text style={{ width: "100%" }}>{getBlankWord(`${t('purchase.mobilePrint', { lng: "zh" })}(${t('purchase.mobilePrint', { lng: "vn" })}):` + getReceiverInfo(selRows[0].sourceType === SourceTypeEnum.PURCHASE_ITEM ? selRows[0]?.source?.order || null : null).mobilePrint, true)}</Text></View>
            <View style={{ ...styles.tableTopTitle, width: '14%', textAlign: 'left' }}><Text style={{ width: "100%" }}>{getBlankWord(`${t('purchase.recver', { lng: "zh" })}(${t('purchase.recver', { lng: "vn" })}):` + getReceiverInfo(selRows[0].sourceType === SourceTypeEnum.PURCHASE_ITEM ? selRows[0]?.source?.order || null : null).recver, true)}</Text></View>
          </View>
          <View style={{ flexDirection: 'row', borderLeftWidth: 1 }}>
            <View style={{ ...styles.tableCol, width: '6%', borderTopWidth: 0 }}>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell }}>{getBlankWord(t('purchase.seq', { lng: "zh" }))}</Text>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell }}>{getBlankWord(t('purchase.seq', { lng: "vn" }))}</Text>
            </View>
            <View style={{ ...styles.tableCol, width: '22%' }}>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell }}>{
                printType !== PrintType.OUT ? t('purchase.sendNum', { lng: "zh" })
                  : t('purchase.outLot', { lng: "zh" })
              }</Text>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell }}>{
                printType !== PrintType.OUT ? t('purchase.sendNum', { lng: "vn" })
                  : t('purchase.outLot', { lng: "vn" })
              }</Text>
            </View>
            <View style={{ ...styles.tableCol, width: '12%' }}>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printCode', { lng: "zh" }))}</Text>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printCode', { lng: "vn" }))}</Text>
            </View>
            <View style={{ ...styles.tableCol, width: '8%' }}>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printName', { lng: "zh" }))}</Text>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printName', { lng: "vn" }))}</Text>
            </View>
            <View style={{ ...styles.tableCol, width: '20%' }}>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printModel', { lng: "zh" }))}</Text>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printModel', { lng: "vn" }))}</Text>
            </View>
            <View style={{ ...styles.tableCol, width: '4%' }}>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printUnit', { lng: "zh" }))}</Text>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printUnit', { lng: "vn" }))}</Text>
            </View>
            <View style={{ ...styles.tableCol, width: '7%' }}>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printNum', { lng: "zh" }))}</Text>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printNum', { lng: "vn" }))}</Text>
            </View>
            <View style={{ ...styles.tableCol, width: '7%' }}>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.qcNum', { lng: "zh" }))}</Text>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.qcNum', { lng: "vn" }))}</Text>
            </View>
            <View style={{ ...styles.tableCol, width: '7%' }}>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.qcNo', { lng: "zh" }))}</Text>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.qcNo', { lng: "vn" }))}</Text>
            </View>
            <View style={{ ...styles.tableCol, width: '7%' }}>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.qcOk', { lng: "zh" }))}</Text>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.qcOk', { lng: "vn" }))}</Text>
            </View>
          </View>
          {/* 表身 */}
          {selRows.map((item, rowIndex) => (
            <View style={[styles.tableRow, { borderLeftWidth: 1 }]} key={rowIndex} wrap={false}>
              <View style={{ ...styles.tableCol, width: '6%' }}><Text style={styles.fontStyle}>{getBlankWord(rowIndex + 1 + '')}</Text></View>
              <View style={{ ...styles.tableCol, width: '22%', flexDirection: "column" }}>
                <Text style={{ ...styles.fontStyle, letterSpacing: -1, }}>{getBlankWord(item.lot, true)}</Text>
                <Text style={{ ...styles.fontStyle, letterSpacing: 0 }}>{getSourceNumber(item)}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '12%' }}><Text style={{ ...styles.fontStyle, letterSpacing: -1 }}>{getBlankWord(item?.product?.code || purOrder?.product?.code || '', true)}</Text></View>
              <View style={{ ...styles.tableCol, width: '8%' }}><Text style={{ ...styles.fontStyle, textAlign: 'left', letterSpacing: -1 }}>{getBlankWord(item?.product?.name || purOrder?.product?.name || '', true)}</Text></View>
              <View style={{ ...styles.tableCol, width: '20%' }}><Text style={{ ...styles.fontStyle, textAlign: 'left', width: "100%", letterSpacing: -1 }}>{getBlankWord(truncateString(item?.product?.model || purOrder?.product?.model || '', 38), true)}</Text></View>
              <View style={{ ...styles.tableCol, width: '4%' }}><Text style={{ ...styles.fontStyle, textAlign: 'left', width: "100%", letterSpacing: -1 }}>{getBlankWord(item?.product?.uom || purOrder?.product?.uom || '')}</Text></View>
              <View style={{ ...styles.tableCol, width: '7%' }}><Text style={{ ...styles.fontStyle, textAlign: 'left', width: "100%", letterSpacing: -1 }}>{getBlankWord(numFormmater(Number(item.count)), true)}</Text></View>
              <View style={{ ...styles.tableCol, width: '7%' }}></View>
              <View style={{ ...styles.tableCol, width: '7%' }}></View>
              <View style={{ ...styles.tableCol, width: '7%' }}></View>
            </View>
          ))}
        </View>

        <View style={styles.bottomInfo}>
          <View style={styles.bottomItem}>
            <Text style={styles.bottomText}>{`${t('purchase.qc', { lng: "zh" })}(${t('purchase.qc', { lng: "vn" })})`}</Text>
          </View>
          <View style={styles.bottomItem}>
            <Text style={styles.bottomText}>{`${t('purchase.pg', { lng: "zh" })}(${t('purchase.pg', { lng: "vn" })})`}</Text>
          </View>
          <View style={styles.bottomItem}>
            <Text style={styles.bottomText}>{`${t('purchase.cg', { lng: "zh" })}(${t('purchase.cg', { lng: "vn" })})`}</Text>
          </View>
        </View>
        <View style={{ marginTop: 30, flexDirection: "row", justifyContent: "flex-end" }}>
          <Text style={{ bottom: 16, right: 4, fontSize: 8 }} >{printerStr}</Text>
        </View>
      </Page>

    </Document >
  }

  const handlePDF = async (flag: boolean) => {
    if (selectedRows.length <= 0) {
      return notification.error({
        placement: 'top',
        message: t('common.addError'),
        description: t('purchase.printSelectTip'),
      });
    }
    setLoading(true)
    onStart()

    try {
      // 生成采购单号或需求单号条码
      let purOrderBarcode: string | undefined;
      if (printType === PrintType.PURCHASE && purOrderNo) {
        try {
          const barcodeData = await handleCode(purOrderNo, purOrderNo);
          purOrderBarcode = barcodeData.codeUrl;
        } catch (error) {
          console.error('采购单号条码生成失败:', error);
        }
      } else if (printType === PrintType.OUT && showBottom && selectedRows[0]?.order?.number) {
        // 当是OUT类型且showBottom为true时，生成订单号条码
        try {
          const orderNumber = selectedRows[0].order.number;
          const barcodeData = await handleCode(orderNumber, orderNumber);
          purOrderBarcode = barcodeData.codeUrl;
        } catch (error) {
          console.error('订单号条码生成失败:', error);
        }
      }

      let codeRows: CodeUrlOrder[] = [];
      for (let i = 0; i < selectedRows.length; i++) {
        // 根据 printType 决定条码内容
        const barcodeContent = printType === PrintType.OUT ? selectedRows[i]?.lot ?? '' : selectedRows[i]?.order?.number;
        const displayText = printType === PrintType.OUT ? selectedRows[i]?.lot ?? '' : selectedRows[i]?.order?.number;

        let codeObj = await handleCode(barcodeContent, displayText);
        let codeRow: CodeUrlOrder = {
          ...selectedRows[i],
          codeStr: codeObj.codeStr,
          codeUrl: codeObj.codeUrl
        }
        codeRows.push({ ...codeRow })
      }
      if (!!!purOrderBarcode) {
        let c = selectedRows?.[0]?.order?.number ?? ''
        let cObj = await handleCode(c, c);
        purOrderBarcode = cObj.codeUrl
      }
      let pageEle = flag ? MyVerticalDocument(codeRows, purOrderBarcode) : MyDocument(codeRows, purOrderBarcode)
      if (!pageEle) {
        notification.error({ placement: 'top', message: t('purchase.printErr') });
        setLoading(false);
        return
      }
      // Generate PDF as Blob in the browser

      const blob = await pdf(pageEle).toBlob();
      let name = `${selectedRows[0].order.number}(${t('purchase.checkoutOrder')})-${printerStr}(${flag ? t('warehouse.verticalPrint') : t('warehouse.normalPrint')}).pdf`;
      let docId = selectedRows[0].order.docId;
      if (docId) {
        let file = await upload({ name, size: blob.size, file: blob });
        if (file) {
          let doc = await docAdd({ pId: docId, name, suffix: file.suffix, nodeType: DocTypeEnum.LEAF, fileId: file.id });
          await orderQcPrint(selectedRows[0].orderId, doc.id);
        }
      }
      const pdfUrl = URL.createObjectURL(blob);
      // 创建一个 iframe 并将 PDF URL 作为其源
      const iframe = document.createElement('iframe');

      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = pdfUrl;

      // 将 iframe 添加到 body 中
      document.body.appendChild(iframe);
      iframe.onload = () => {
        console.log('PDF 加载完成，等待额外时间');
        try {
          console.log('最终调用 print()');
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
          // window.addEventListener("afterprint", handlePrintEnd);
        } catch (error) {
          console.error('打印失败:', error);
        } finally {
          setLoading(false)
          onEnd()
        }
      };

    } catch (error) {
      console.error('上传失败:', error);
      setLoading(false)
      onEnd()
    }
  };

  return (
    <Dropdown trigger={["click"]}
      menu={{
        items: [
          {
            key: 'NORM',
            label: t('warehouse.normalPrint'),
            onClick: () => handlePDF(false),

          },
          {
            key: 'WARE',
            label: <Tooltip title={t('layout.printTip', { width: settingWitdh, height: settingHeight })}>{t('warehouse.verticalPrint')}</Tooltip>,
            onClick: () => handlePDF(true),
          },
        ],
      }}
    >
      <Button type="primary" loading={loading} disabled={disabled}>{t('common.printInfoQC')}</Button>
    </Dropdown>);
};

export default PrintQC;